import React, { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { Search, ArrowRightLeft, Building2, User, MapPin, AlertCircle, CheckCircle } from 'lucide-react';

interface Unit {
  id: string;
  name: string;
  is_active: boolean;
}

interface Contract {
  id: string;
  code: string;
  client_name: string;
  cpf: string;
  loan_amount: number | null;
  outstanding_balance: number | null;
  ruta: number;
  created_at: string;
  unit_id: string;
  unit_name?: string;
  status: string;
}

export default function ClientTransfer() {
  const { user } = useAuth();
  const [units, setUnits] = useState<Unit[]>([]);
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [filteredContracts, setFilteredContracts] = useState<Contract[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUnitFilter, setSelectedUnitFilter] = useState<string>('all');
  const [selectedContract, setSelectedContract] = useState<Contract | null>(null);
  const [targetUnitId, setTargetUnitId] = useState<string>('');
  const [targetRuta, setTargetRuta] = useState<number>(1);
  const [transferring, setTransferring] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
  const [showTransferModal, setShowTransferModal] = useState(false);

  useEffect(() => {
    if (user?.role === 'admin') {
      fetchData();
    }
  }, [user]);

  useEffect(() => {
    filterContracts();
  }, [contracts, searchTerm, selectedUnitFilter]);

  const fetchData = async () => {
    try {
      setLoading(true);

      const { data: unitsData, error: unitsError } = await supabase
        .from('units')
        .select('id, name, is_active')
        .order('name');

      if (unitsError) throw unitsError;
      setUnits(unitsData || []);

      const { data: contractsData, error: contractsError } = await supabase
        .from('contracts')
        .select(`
          id,
          code,
          client_name,
          cpf,
          loan_amount,
          outstanding_balance,
          ruta,
          created_at,
          unit_id,
          status,
          units(name)
        `)
        .order('created_at', { ascending: false });

      if (contractsError) throw contractsError;

      const contractsWithUnitName = (contractsData || []).map(c => ({
        ...c,
        unit_name: (c.units as any)?.name || 'N/A'
      }));

      console.log('Loaded contracts:', contractsWithUnitName.length);
      console.log('Sample contract:', contractsWithUnitName[0]);
      setContracts(contractsWithUnitName);
    } catch (error) {
      console.error('Error fetching data:', error);
      setMessage({ type: 'error', text: 'Erro ao carregar dados' });
    } finally {
      setLoading(false);
    }
  };

  const filterContracts = () => {
    let filtered = contracts;

    if (selectedUnitFilter !== 'all') {
      filtered = filtered.filter(c => c.unit_id === selectedUnitFilter);
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(c =>
        c.client_name.toLowerCase().includes(term) ||
        c.code.toLowerCase().includes(term) ||
        c.cpf.includes(term)
      );
    }

    setFilteredContracts(filtered);
  };

  const handleOpenTransferModal = (contract: Contract) => {
    setSelectedContract(contract);
    setTargetUnitId(contract.unit_id);
    setTargetRuta(contract.ruta);
    setShowTransferModal(true);
    setMessage(null);
  };

  const handleTransfer = async () => {
    if (!selectedContract || !targetUnitId) return;

    if (targetUnitId === selectedContract.unit_id && targetRuta === selectedContract.ruta) {
      setMessage({ type: 'error', text: 'Selecione uma unidade ou ruta diferente' });
      return;
    }

    setTransferring(true);
    setMessage(null);

    try {
      const updates: any = {};

      if (targetUnitId !== selectedContract.unit_id) {
        updates.unit_id = targetUnitId;
      }

      if (targetRuta !== selectedContract.ruta) {
        updates.ruta = targetRuta;
      }

      const { error: contractError } = await supabase
        .from('contracts')
        .update(updates)
        .eq('id', selectedContract.id);

      if (contractError) throw contractError;

      const { error: installmentsError } = await supabase
        .from('installments')
        .update(updates)
        .eq('contract_id', selectedContract.id);

      if (installmentsError) throw installmentsError;

      const { error: evaluationsError } = await supabase
        .from('contract_evaluations')
        .update(updates)
        .eq('contract_id', selectedContract.id);

      if (evaluationsError) throw evaluationsError;

      setMessage({
        type: 'success',
        text: `Cliente ${selectedContract.client_name} transferido com sucesso!`
      });

      setTimeout(() => {
        setShowTransferModal(false);
        setSelectedContract(null);
        fetchData();
      }, 2000);

    } catch (error) {
      console.error('Error transferring client:', error);
      setMessage({ type: 'error', text: 'Erro ao transferir cliente' });
    } finally {
      setTransferring(false);
    }
  };

  if (user?.role !== 'admin') {
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-600" />
          <p className="text-red-800">Acesso negado. Esta página é restrita a administradores.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-8">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Transferência de Clientes</h1>
        <p className="text-gray-600">Gerencie e transfira clientes entre unidades e rutas</p>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Buscar Cliente
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Nome, CPF ou código do contrato"
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Filtrar por Unidade
            </label>
            <select
              value={selectedUnitFilter}
              onChange={(e) => setSelectedUnitFilter(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">Todas as Unidades</option>
              {units.map(unit => (
                <option key={unit.id} value={unit.id}>{unit.name}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="mt-4 flex items-center gap-4 text-sm text-gray-600">
          <span className="flex items-center gap-2">
            <Building2 className="w-4 h-4" />
            {units.length} unidades
          </span>
          <span className="flex items-center gap-2">
            <User className="w-4 h-4" />
            {filteredContracts.length} clientes encontrados
          </span>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Cliente
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  CPF
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Código
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Unidade
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ruta
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Empréstimo
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Saldo
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredContracts.map((contract) => (
                <tr key={contract.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="font-medium text-gray-900">{contract.client_name}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {contract.cpf}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {contract.code}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-900">{contract.unit_name}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded">
                      Ruta {contract.ruta}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    R$ {(contract.loan_amount || 0).toFixed(2)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    R$ {(contract.outstanding_balance || 0).toFixed(2)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => handleOpenTransferModal(contract)}
                      className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800"
                    >
                      <ArrowRightLeft className="w-4 h-4" />
                      Transferir
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {filteredContracts.length === 0 && (
            <div className="text-center py-12">
              <User className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">Nenhum cliente encontrado</h3>
              <p className="mt-1 text-sm text-gray-500">
                Tente ajustar os filtros de busca
              </p>
            </div>
          )}
        </div>
      </div>

      {showTransferModal && selectedContract && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Transferir Cliente</h3>

            <div className="mb-6 p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600 mb-1">Cliente</p>
              <p className="font-medium text-gray-900">{selectedContract.client_name}</p>
              <p className="text-sm text-gray-600 mt-2">Código: {selectedContract.code}</p>
            </div>

            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Unidade de Destino
                </label>
                <select
                  value={targetUnitId}
                  onChange={(e) => setTargetUnitId(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {units.map(unit => (
                    <option key={unit.id} value={unit.id}>
                      {unit.name} {unit.id === selectedContract.unit_id ? '(Atual)' : ''}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ruta de Destino
                </label>
                <select
                  value={targetRuta}
                  onChange={(e) => setTargetRuta(Number(e.target.value))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {[1, 2, 3, 4, 5, 6, 7, 8].map(ruta => (
                    <option key={ruta} value={ruta}>
                      Ruta {ruta} {ruta === selectedContract.ruta ? '(Atual)' : ''}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {message && (
              <div className={`mb-4 p-3 rounded-lg flex items-center gap-2 ${
                message.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
              }`}>
                {message.type === 'success' ? (
                  <CheckCircle className="w-5 h-5" />
                ) : (
                  <AlertCircle className="w-5 h-5" />
                )}
                <p className="text-sm">{message.text}</p>
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowTransferModal(false);
                  setSelectedContract(null);
                  setMessage(null);
                }}
                disabled={transferring}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleTransfer}
                disabled={transferring || (targetUnitId === selectedContract.unit_id && targetRuta === selectedContract.ruta)}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {transferring ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Transferindo...
                  </>
                ) : (
                  <>
                    <ArrowRightLeft className="w-4 h-4" />
                    Transferir
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
